import ControlViewModel from '@/ViewModels/ControlViewModel';
import RADApplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import NavigationService from '@/Management/NavigationService';
import NavigationHelper from '@/Management/NavigationHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

import MenuItemViewModel from '@/ViewModels/Controls/MenuItemViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';

export default class MenuViewModel extends ControlViewModel {
    
    /*
     * Gibt die Öffnungsrichtung des Menüs an. Kann "top", "bottom", "left", "right" sein.  
     */
    public direction: KnockoutObservable<string>;

    /*
     * Ausrichtung des Menüs. Kann "horizontal" oder "vertical" sein.
     */
    public orientation: KnockoutObservable<string>;

    /*
     * Gibt an, ob es sich um ein ApplicationMenu handelt
     */
    public isApplicationMenu: KnockoutObservable<boolean>;

    /*
     * Die Items des Menu
     * @type {[MenuItemViewModel]}
     */
    public items: KnockoutObservableArray<MenuItemViewModel>;

    constructor(params: any) {
        super(params);

        this.direction = this.observableOrDefault(params.direction, 'default');
        this.orientation = this.observableOrDefault(params.orientation, 'horizontal');
        this.isApplicationMenu = this.observableOrDefault(params.isApplicationMenu, false);
        this.items = this.observableOrDefault(params.items, []) as KnockoutObservableArray<MenuItemViewModel>;

        ControlViewModel.checkProperties(this, params);
    }

    public closeAllItems(): void {
        const items = this.items();
        for (let i = 0; i < this.items().length; i++) {
            const item = items[i]
            item.isOpen.valueWillMutate();
            item.isOpen(false);
            item.isOpen.valueHasMutated();
        }
    }
    
    private static createMenuItem(item: any, showImages: boolean, parentLocator: string): MenuItemViewModel {
        const MenuItemViewModelType = ComponentManager.getViewModel<typeof MenuItemViewModel>('MenuItemViewModel');
        const LinkViewModelType = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
        const ImageViewModelType = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const LabelViewModelType = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
        
        let image: ImageViewModel | null = null;

        const itemLocatorRel = `[${item.name}]`;
        const itemLocator = `${parentLocator}.${itemLocatorRel}`;

        if (showImages && !MenuViewModel.isEmpty(item.iconUrl)) {
            image = new ImageViewModelType({
                parentLocator: itemLocator,
                locator: 'link.image',
                source: item.iconUrl,
                width: 16,
                height: 16
            });
        }

        const items: MenuItemViewModel[] | null = this.createMenuChildItems(item.childNodes, itemLocator);

        return new MenuItemViewModelType({
            parentLocator: parentLocator,
            locator: itemLocatorRel,
            isVisible: true,
            link: new LinkViewModelType({
                parentLocator: itemLocator,
                locator: 'link',
                image: image,
                label: new LabelViewModelType({
                    parentLocator: itemLocator,
                    locator: 'link.label',
                    ergName: item.ergName,
                    caption: item.caption
                }),
                onClick: (openInNewWindow: boolean) => {
                    if (item.action === 'CUSTOM_ACTION') {
                        item.actionCallback();
                    } else if (!String.isNullOrEmpty(item.action)) {
                        NavigationService.getInstance().loadViewFactory(item.action);
                    } else if (!String.isNullOrEmpty(item.url)) {
                        NavigationService.getInstance().navigateToUrl(item.url, null, null, null, null, openInNewWindow);
                    } else if (item.isListView === true) {
                        NavigationHelper.openListViewById(item.viewPDOID, null, openInNewWindow);
                    } else if (!String.isNullOrEmpty(item.pdTypeName)) {
                        NavigationHelper.openDetailView('EDIT', item.pdTypeName, null, openInNewWindow);
                    }
                }
            }),
            items: items
        });
    }
    
    private static createMenuChildItems(items: any[], parentLocator: string): MenuItemViewModel[] {
        const menuItems: MenuItemViewModel[] = [];

        if (items != null) {
            for (let i = 0; i < items.length; i++) {
                const item = this.createMenuItem(items[i], true, parentLocator);
                menuItems.push(item);
            }
        }

        return menuItems;
    }
        
    public static parseUserMenuTree(): MenuViewModel {
        const menuLocator = 'user-menu';
        const items = ko.computed(() => {
            const menuItems: MenuItemViewModel[] = [];
            const menus = RADApplication.getInstance().user.menus();
            const menuTree = menus.userMenuTree;

            if (menuTree != null) {
                for (let i = 0; i < menuTree.childNodes.length; i++) {
                    const item = this.createMenuItem(menuTree.childNodes[i], false, menuLocator);
                    menuItems.push(item);
                }
            }

            return menuItems;
        });

        const menuVM = new MenuViewModel({
            locator: menuLocator,
            items: items,
            isApplicationMenu: true
        });
        menuVM.addComputed(items);

        return menuVM;
    }

    public static createStaticMenu(): MenuViewModel {
        const MenuItemViewModelType = ComponentManager.getViewModel<typeof MenuItemViewModel>('MenuItemViewModel');
        const LinkViewModelType = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
        const ImageViewModelType = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
        const LabelViewModelType = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

        const menuLocator = 'static-menu';

        return new MenuViewModel({
            locator: menuLocator,
            items: (() => {
                const result: MenuItemViewModel[] = [];

                result.push(new MenuItemViewModelType({
                    parentLocator: menuLocator,
                    locator: 'home',
                    link: new LinkViewModelType({
                        parentLocator: menuLocator,
                        locator: 'home.link',
                        image: new ImageViewModelType({
                            parentLocator: menuLocator,
                            locator: 'home.link.image',
                            source: GlobalConfiguration.inlineImages.MENU_HOME,
                            alternativeText: new LabelViewModelType({
                                ergName: 'MENU_ITEM_HOME'
                            })
                        }),
                        label: new LabelViewModelType({
                            parentLocator: menuLocator,
                            locator: 'home.link.label',
                            ergName: 'MENU_ITEM_HOME'
                        }),
                        onClick: (openInNewWindow: boolean) => {
                            NavigationService.getInstance().navigateToHome(openInNewWindow);
                        }
                    })
                }));

                if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowHelpButton')) {
                    result.push(new MenuItemViewModelType({
                        parentLocator: menuLocator,
                        locator: 'help',
                        link: new LinkViewModelType({
                            parentLocator: menuLocator,
                            locator: 'help.link',
                            image: new ImageViewModelType({
                                parentLocator: menuLocator,
                                locator: 'help.link.image',
                                source: 'lifebelt.png',
                                alternativeText: new LabelViewModelType({
                                    ergName: 'MENU_ITEM_HELP'
                                })
                            }),
                            label: new LabelViewModelType({
                                parentLocator: menuLocator,
                                locator: 'help.link.label',
                                ergName: 'MENU_ITEM_HELP'
                            }),
                            onClick: ()=>  {
                                RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createNoticeMessageBox(null, 'HELP_TEXT'));
                            }
                        })
                    }));
                }

                if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowAboutButton')) {
                    result.push(new MenuItemViewModelType({
                        parentLocator: menuLocator,
                        locator: 'info',
                        link: new LinkViewModelType({
                            parentLocator: menuLocator,
                            locator: 'info.link',
                            image: new ImageViewModelType({
                                parentLocator: menuLocator,
                                locator: 'info.link.image',
                                source: GlobalConfiguration.inlineImages.MENU_INFO,
                                alternativeText: new LabelViewModelType({
                                    ergName: 'MENU_ITEM_INFO'
                                })
                            }),
                            label: new LabelViewModelType({
                                parentLocator: menuLocator,
                                locator: 'info.link.label',
                                ergName: 'MENU_ITEM_INFO'
                            }),
                            onClick:  () => {
                                RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createNoticeMessageBox(null, 'INFO_TEXT', null, { width: 400, technicalName: 'application-info' }));
                            }
                        })
                    }));
                }

                // TODO: Kann das hier weg?!
                if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTemplateButton')) {
                    result.push(new MenuItemViewModelType({
                        parentLocator: menuLocator,
                        locator: 'template',
                        link: new LinkViewModelType({
                            parentLocator: menuLocator,
                            locator: 'template.link',
                            image: new ImageViewModelType({
                                parentLocator: menuLocator,
                                locator: 'template.link.image',
                                source: 'brush4.png'
                            }),
                            label: new LabelViewModelType({
                                parentLocator: menuLocator,
                                locator: 'template.link.label',
                                ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.META.PDAPPLICATIONMETA_TEMPLATESTYLE_ERGNAME'
                            }),
                            onClick: () => {
                                NavigationService.getInstance().loadViewFactory('TemplateStyle');
                            }
                        })
                    }));
                }

                result.push(new MenuItemViewModelType({
                    parentLocator: menuLocator,
                    locator: 'logout',
                    link: new LinkViewModelType({
                        parentLocator: menuLocator,
                        locator: 'logout.link',
                        image: new ImageViewModelType({
                            parentLocator: menuLocator,
                            locator: 'logout.link.image',
                            source: 'Logout.png',
                            alternativeText: new LabelViewModelType({
                                ergName: 'MENU_LOGOUT_NODE_NAME'
                            })
                        }),
                        label: new LabelViewModelType({
                            parentLocator: menuLocator,
                            locator: 'logout.link.label',
                            ergName: 'MENU_LOGOUT_NODE_NAME'
                        }),
                        onClick:  () => {
                            NavigationService.getInstance().navigateToLogout();
                        }
                    })
                }));

                return result;
            })(),
            isApplicationMenu: true
        });
    }

    private static isEmpty(url: string): boolean {
        return url === '$$DATA_HANDLER$$?ID=BLANK';
    }
}

ComponentManager.setViewModel('MenuViewModel', MenuViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');